import React, { useState } from "react";
import { X, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { reportsApi } from "../lib/events";
import { extractError } from "../lib/api";

/**
 * ResendOrderConfirmModal — re-send the WHOLE existing order confirmation
 * (all active tickets) to the ORIGINAL BUYER email. This is deliberately
 * separate from the per-ticket "Resend ticket" action.
 *
 * Never creates tickets, regenerates QR codes, or changes attendee / buyer /
 * totals / payment / check-in state. When the buyer email is hidden for
 * privacy, the address is never displayed here or in the success toast.
 */
export function ResendOrderConfirmModal({ eventId, order, onClose, onResent }) {
  const [busy, setBusy] = useState(false);
  if (!order) return null;

  const piiHidden = !!order.pii_hidden;
  const buyerEmail = order.buyer_email || null;

  const onConfirm = async () => {
    if (busy) return; // double-click guard
    setBusy(true);
    try {
      const res = await reportsApi.resendOrderConfirmation(eventId, order.id);
      if (res.suppressed) {
        toast.info(res.message || "This order confirmation was just resent — please wait a moment.");
      } else {
        toast.success("Order confirmation resent successfully.");
      }
      onResent?.(res);
      onClose?.();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="resend-order-confirm-modal"
    >
      <div
        className="bg-white border-2 border-[var(--gt-ink)] shadow-[8px_8px_0px_0px_var(--gt-ink)] w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-[var(--gt-ink)]">
          <div>
            <div className="gt-label flex items-center gap-1"><Mail size={13} /> Resend order confirmation</div>
            <h3 className="font-heading text-2xl font-black tracking-tighter mt-1">Send to buyer</h3>
            <div className="text-xs text-[var(--gt-ink-3)] mt-1 font-mono">Order {order.id.slice(0, 8)}</div>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--gt-ink-3)] hover:text-[var(--gt-ink)]"
            data-testid="resend-order-confirm-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5" data-testid="resend-order-confirm-body">
          {piiHidden || !buyerEmail ? (
            <p className="text-sm text-[var(--gt-ink-2)]" data-testid="resend-order-confirm-question">
              Resend this order confirmation to <strong>the original buyer</strong>?
              The existing tickets (with any corrected attendee details) are
              re-sent as one email. Nothing is regenerated or changed.
            </p>
          ) : (
            <p className="text-sm text-[var(--gt-ink-2)]" data-testid="resend-order-confirm-question">
              Resend this order confirmation to <strong>{buyerEmail}</strong>?
              The existing tickets (with any corrected attendee details) are
              re-sent as one email. Nothing is regenerated or changed.
            </p>
          )}

          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="gt-btn-secondary"
              data-testid="resend-order-confirm-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="gt-btn-primary inline-flex items-center gap-2"
              data-testid="resend-order-confirm-submit"
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              Resend confirmation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResendOrderConfirmModal;
